import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../../../services/api";

// Shared prefix for every view of a user's assignments, so one refetch refreshes
// all of them. Each view must still append its own segment: React Query keeps a
// single cache entry per key, and infinite `{ pages, pageParams }` data is not
// interchangeable with a flat list of assignments.
export const userCertificationsQueryKey = (userId?: string) => [
  "certifications-assignments-user-dashboard",
  userId,
];

const EMPTY_ASSIGNMENTS: any[] = [];

export const useUserCertifications = (userId?: string) => {
  const {
    data: assignmentData,
    isLoading: isLoadingCertifications,
    isFetching: isFetchingCertifications,
    refetch: refetchCertifications,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [...userCertificationsQueryKey(userId), "infinite"],
    queryFn: ({ pageParam }) => api.getAssignments({ after: pageParam }),
    getNextPageParam: (lastPage) => lastPage?.data?.links?.after || undefined,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    cacheTime: 0,
    enabled: !!userId,
  });

  const certifications = useMemo(
    () =>
      assignmentData?.pages?.flatMap((page) => page?.data?.data ?? []) ?? [],
    [assignmentData?.pages]
  );

  return {
    certifications,
    isLoadingCertifications,
    isFetchingCertifications,
    refetchCertifications,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

const fetchAllAssignments = async () => {
  const assignments: any[] = [];
  let after: string | undefined;

  while (true) {
    const response = await api.getAssignments({ after });
    assignments.push(...(response?.data?.data ?? []));
    const next = response?.data?.links?.after || undefined;
    // A repeated cursor would refetch the same page forever.
    if (!next || next === after) break;
    after = next;
  }

  return assignments;
};

// For views that search and sort on the client: those controls can only be
// trusted against the full list, so walk the cursor to exhaustion before
// resolving rather than handing back a truncated first page.
export const useAllUserCertifications = (userId?: string) => {
  const {
    data,
    isLoading: isLoadingCertifications,
    isFetching: isFetchingCertifications,
    refetch: refetchCertifications,
  } = useQuery({
    queryKey: [...userCertificationsQueryKey(userId), "all-pages"],
    queryFn: fetchAllAssignments,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: !!userId,
  });

  return {
    certifications: data ?? EMPTY_ASSIGNMENTS,
    isLoadingCertifications,
    isFetchingCertifications,
    refetchCertifications,
  };
};
